import { warnAndNotify } from '@execonline-inc/error-reporting.private';
import { isJust, Maybe as StructuredMaybe } from '@execonline-inc/structured-maybe';
import { assertNever } from '@kofno/piper';
import { Task } from 'taskarian';
import LearningCollectionStore from '.';
import { callApi } from '../../../../../Appy';
import { findLinkT } from '../../../../../LinkyLinky';
import ReactionComponent from '../../../../../ReactionComponent';
import { Link } from '../../../../../Resource/Types';
import ModalStore from '../../../../Modal/Store';
import { learningCollectionSummaryResourceDecoder } from '../../../Decoders';
import { LearningCollectionSummaryResource } from '../../../Types';
import { LearningCollectionError, LearningCollectionState } from './Types';

interface Props {
  store: LearningCollectionStore;
}

interface LearningCollectionPublishingOut {
  published: boolean;
}

const toggledPublishedValue = (publishedAt: StructuredMaybe<Date>): boolean => !isJust(publishedAt);

export const learningCollectionPublishingEncoder = (
  publishedAt: StructuredMaybe<Date>,
): LearningCollectionPublishingOut => ({
  published: toggledPublishedValue(publishedAt),
});

const handleReportsError = (store: LearningCollectionStore) => (error: LearningCollectionError) => {
  store.error(error);
  warnAndNotify('LearningCollectionReactions', error.kind, error);
};

const hideLearningCollectionSuccess =
  (store: LearningCollectionStore, hideModalStore: ModalStore) =>
  (_learningCollectionSummaryResource: LearningCollectionSummaryResource) => {
    hideModalStore.close();
    store.learningCollectionsStore.updateLearningCollectionsList();
  };

const learningCollectionSuccess =
  (store: LearningCollectionStore) =>
  (learningCollectionSummaryResource: LearningCollectionSummaryResource) => {
    store.ready(learningCollectionSummaryResource);
  };

class LearningCollectionReactions extends ReactionComponent<
  LearningCollectionStore,
  LearningCollectionState,
  Props
> {
  tester = () => this.props.store.state;
  effect = (state: LearningCollectionState) => {
    const { store } = this.props;
    switch (state.kind) {
      case 'waiting':
        break;
      case 'loading':
        store.ready(state.learningCollectionSummaryResource);
        break;
      case 'ready':
        break;
      case 'hide':
        Task.succeed<LearningCollectionError, ReadonlyArray<Link>>(store.links)
          .andThen(findLinkT('hide-learning-collection'))
          .andThen(callApi(learningCollectionSummaryResourceDecoder, {}))
          .fork(
            handleReportsError(store),
            hideLearningCollectionSuccess(store, state.hideModalStore),
          );
        break;
      case 'publishing':
        Task.succeed<LearningCollectionError, ReadonlyArray<Link>>(store.links)
          .andThen(findLinkT('publish'))
          .andThen(
            callApi(
              learningCollectionSummaryResourceDecoder,
              learningCollectionPublishingEncoder(
                state.learningCollectionSummaryResource.payload.publishedAt,
              ),
            ),
          )
          .fork(handleReportsError(store), learningCollectionSuccess(store));
        break;
      case 'error':
        break;
      default:
        assertNever(state);
    }
  };
}

export default LearningCollectionReactions;
